import { z } from 'zod';
import { serverNow } from '@/lib/server-clock.js';
import {
  REPORT_KINDS,
  TRANSFER_DIRECTIONS,
  type ReportFilter,
  type ReportKind,
} from '@transfers-system/contracts';
import { zonedDateParts } from '@/lib/formatters.js';

/**
 * transfers.md section 9: every filter (period, branch, employee, box, bank
 * account, customer) lives in the URL search params so a report can be
 * reopened with the same view (validated with zod, per TanStack Router's file
 * route `validateSearch`). `tab` selects which of the six reports is active.
 */
export const reportsSearchSchema = z.object({
  tab: z.enum(REPORT_KINDS).catch('summary'),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  branchId: z.string().optional().catch(undefined),
  employeeId: z.string().optional().catch(undefined),
  boxId: z.string().optional().catch(undefined),
  bankAccountId: z.string().optional().catch(undefined),
  customer: z.string().optional().catch(undefined),
  direction: z.enum(TRANSFER_DIRECTIONS).optional().catch(undefined),
});
export type ReportsSearch = z.infer<typeof reportsSearchSchema>;

const TIMEZONE = 'Asia/Amman';

const pad = (value: number): string => String(value).padStart(2, '0');

/** transfers.md section 9: the filter bar's default period is the current month in Asia/Amman. */
export function currentMonthRange(now: Date = serverNow()): { from: string; to: string } {
  const parts = zonedDateParts(now, TIMEZONE);
  const year = Number(parts.year);
  const month = Number(parts.month);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    from: `${parts.year}-${parts.month}-01`,
    to: `${parts.year}-${parts.month}-${pad(lastDay)}`,
  };
}

/** Every report kind is a leaf of التقارير - one page, six tab strips. */
export const REPORT_TAB_ORDER: readonly ReportKind[] = REPORT_KINDS;

/** Applies the current-month default only where the URL carries no period yet. */
export function withDefaultPeriod(search: ReportsSearch): ReportsSearch {
  if (search.from && search.to) return search;
  const defaults = currentMonthRange();
  return { ...search, from: search.from ?? defaults.from, to: search.to ?? defaults.to };
}

/** The filter shape every report endpoint accepts (packages/contracts reports/contract.ts). */
export function toReportFilter(search: ReportsSearch): ReportFilter {
  return {
    from: search.from,
    to: search.to,
    branchId: search.branchId,
    employeeId: search.employeeId,
    boxId: search.boxId,
    bankAccountId: search.bankAccountId,
    customer: search.customer,
    direction: search.direction,
  };
}

/** Whether any filter differs from the bare default period (for the empty-state message). */
export function hasNonDefaultFilters(search: ReportsSearch): boolean {
  return Boolean(
    search.branchId ||
    search.employeeId ||
    search.boxId ||
    search.bankAccountId ||
    search.customer ||
    search.direction,
  );
}

export const DEFAULT_REPORTS_SEARCH: ReportsSearch = { tab: 'summary' };
